import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { LanguagePack } from 'src/app/model/CatalogProduct';
import { DownloadType } from 'src/app/model/DownloadStatus';
import { LoginState } from 'src/app/model/LoginState';
import { PreFlightRequest } from 'src/app/model/PreFlightRequest';
import { CSRegisteredServerCheck, CSRegisteredServerHost, CSRegisteredServerInstance, DiscoveredProduct, PatchExt, ProductAnalysisRequest, ProductAnalysisType, ProductExt, ProductInstance, SupportedStatusExt, SyncCapabilitiesRequest } from 'src/app/model/Product';
import { Solution, SolutionInfo } from 'src/app/model/SolutionInfo';
import { ActivityCapabilities, PreFlightCheck, PreFlightCheckType, PreFlightProgress, PreFlightProgressTask, TaskStatus } from 'src/app/model/TaskStatus';
import { UploadFileType } from 'src/app/model/Upload';
import { UserInfo } from 'src/app/model/User';
import { FileSizePipe } from 'src/app/pipe/file-size.pipe';
import { ActivityService } from 'src/app/service/activity.service';
import { AgentService } from 'src/app/service/agent.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { SolutionService } from 'src/app/service/solution.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { SelectOption } from '../controls/select/select.component';
import { Tab, TabColor } from '../controls/tabs/tabs.component';
import { WizardStep } from '../controls/wizard/wizard.component';
import { DragDropComponent, UploadModalData } from '../dialogs/drag-drop/drag-drop.component';
import { ListItem } from '../dialogs/list-details/list-details.component';
import { CSProductInfo, LoginCsComponent } from '../dialogs/login-cs/login-cs.component';
import { ObjectDetailsComponent, ObjectDetailsTypes } from '../dialogs/object-details/object-details.component';
import { PreReqCsErrorComponent } from '../dialogs/pre-req-cs-error/pre-req-cs-error.component';
import { ProductInstallType, UpgradePromptResponse } from '../products-details/products-details.component';
import { DownloadItem } from '../products-overview/products-overview.component';
import { PackageRow } from '../systems-enable/systems-enable.component';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { TranslationUtils } from 'src/app/app-translation-utils';
import { ProductDataType } from '../products/products.component';

enum SyncSolutionState {
  SOURCE = "source",
  TYPE = "type",
  TARGET = "target",
  ANALYZE = "analyze",
  DOWNLOAD = "download",
  VALIDATE = "validate",
  SYNC = "synchronize"
}

export enum SyncTab {
  INSTANCE,
  PACKAGES
}

@Component({
  selector: 'app-settings-solutions-sync',
  templateUrl: './settings-solutions-sync.component.html',
  styleUrls: ['./settings-solutions-sync.component.css'],
  standalone: false
})
export class SettingsSolutionsSyncComponent extends BaseComponent implements OnInit, OnDestroy {

  steps: WizardStep[] = [
    { 'id': SyncSolutionState.SOURCE, 'name': $localize`Source` },
    { 'id': SyncSolutionState.TYPE, 'name': $localize`Type` },
    { 'id': SyncSolutionState.TARGET, 'name': $localize`Target` },
    { 'id': SyncSolutionState.ANALYZE, 'name': $localize`Analyze` },
    { 'id': SyncSolutionState.DOWNLOAD, 'name': $localize`Download` },
    { 'id': SyncSolutionState.VALIDATE, 'name': $localize`Validate` },
    { 'id': SyncSolutionState.SYNC, 'name': $localize`Synchronize` }
  ]

  instanceCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn("system", $localize`System`, ColumnType.STRING, true, "21"),
    new DataTableColumn("productName", $localize`Product`, ColumnType.STRING, false, "16"),
    new DataTableColumn("productVersion", $localize`Version`, ColumnType.STRING, false, "15"),
    new DataTableColumn("installationDirectory", $localize`Install Path`, ColumnType.STRING, false, "21"),
    new DataTableColumn("platform", $localize`Platform`, ColumnType.STRING, false, "15"),
    new DataTableColumn("isEnabled", $localize`Status`, ColumnType.STATUSEXT, false, "12"),
  );

  packageCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn('displayType', $localize`Type`, ColumnType.STRING, true, "25"),
    new DataTableColumn('name', $localize`Name`, ColumnType.STRING, false, "25"),
    new DataTableColumn('action', $localize`Action`, ColumnType.STRING, false, "25"),
    new DataTableColumn('status', $localize`Status`, ColumnType.STATUSEXT, false, "25")
  );

  index: number = 0;
  state: SyncSolutionState = SyncSolutionState.SOURCE;
  SyncSolutionState = SyncSolutionState;
  TabColor = TabColor;
  SyncTab = SyncTab;
  activeTab: SyncTab = SyncTab.INSTANCE;
  instanceTab = new Tab('system-sync-instance-tab', $localize`Instances`, undefined, false);
  packagesTab = new Tab('system-sync-packages-tab', $localize`Packages`, undefined, false);
  tabs: Tab[] = [this.instanceTab, this.packagesTab];

  solutionInfo: SolutionInfo = new SolutionInfo();
  solutions: Solution[] = [];

  productCapabilities: any[] = [];

  selectedProduct: any;
  selectedVersion: any;
  selectedCapabilities: any;

  selectedSourceEnv: SelectOption;
  selectedTargetEnv: SelectOption;
  selectedTargetProduct: any;
  selectedType: SelectOption;
  capabilities: ActivityCapabilities;

  sourceProduct: any;

  sourceEnvs: SelectOption[] = [];
  sourceProducts: SelectOption[] = [];
  sourceVersions: SelectOption[] = [];
  sourceAgents: string[] = [];
  sourceProductId: number = -1;
  supportedTypeOptions: SelectOption[] = [];
  targetProduct: any;
  targetEnvs: SelectOption[] = [];
  masterSystem: any;

  supportedProducts: any[] = [];
  supportedVersions: any[] = [];
  supportedTypes: any[] = [];

  enabledCompInstanceCounter: number = 0;
  targetAgents: string[] = [];
  componentInstances: any[] = [];
  instanceRows: DataTableRow[] = [];
  preFlightInstanceRows: DataTableRow[] = [];

  targetProductInstances: Record<string, ProductInstance[]>;
  sourceMasterSystem: any;

  analyzing: boolean = false;
  runningQuickAnalysis: boolean = false;
  progressText: string;

  result: any;

  needsUpdate: boolean = false;;
  canUpdate: boolean = false;
  canSkipWarnings: boolean = false;
  currentVersion: string;
  requiredUpdateName: string;
  requiredPatchLevel: string;
  requiredLangs: any[] = [];
  requiredHotfixes: any[] = [];
  customLangPacks: string[] = [];
  missingCustomLangCodes: any;
  analysisStatus: any;
  messageMap: any;
  messageCount: number = 0;
  missingModuleWarning: boolean = false;
  additionalModuleWarning: boolean = false;
  additionalPatchWarning: boolean = false;
  customLangPackWarning: boolean = false;
  unknownPatchWarning: boolean = false;

  skipAdditionalModules: boolean = false;
  skipMissingModules: boolean = false;
  skipAdditionalPatches: boolean = false;
  skipCustomLangs: boolean = false;
  skipUnknownPatch: boolean = false;

  analysisResultList: ListItem[] = [];

  isAnalysisError: boolean = false;
  isAnalysisSuccess: boolean = false;
  isSynced: boolean = false;
  isError: boolean = false;
  errorMessage: string;

  isOnlineMode: boolean;
  isProxyRequired: boolean;
  itemsToDownload: DownloadItem = new DownloadItem();

  needsDownload: boolean = false;
  packageRows: DataTableRow[] = [];
  validationData: any;
  isValidatingPackages: boolean = false;
  isValidatingPackagesComplete: boolean = false;
  isPackageError: boolean = false;
  isPackageDownloadActive: boolean = false;
  isDownloading: boolean = false;
  updateToInstall: PatchExt;
  patchesToInstall: PatchExt[] = [];
  patchesToBeRemoved: PatchExt[] = [];
  langPacksToInstall: LanguagePack[] = [];

  instances: Record<string, ProductInstance[]>;
  csInfo: CSProductInfo;
  analysisVersion: string;
  preReqRegisteredServerCheck: CSRegisteredServerCheck = new CSRegisteredServerCheck();
  preReqRegisteredServerTask: TaskStatus;
  preReqCheckWaiting: boolean = false;
  preReqCheckInProcess: boolean = false;
  preReqCheckSuccess: boolean = false;

  isInstalling: boolean = false;

  preReqTableCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn("system", $localize`System`, ColumnType.STRING, true, "29", ColumnSort.DESC),
    new DataTableColumn("installPath", $localize`Install path`, ColumnType.STRING, false, "21"),
    new DataTableColumn("version", $localize`Version`, ColumnType.STRING, false, "13"),
    new DataTableColumn("platform", $localize`Platform`, ColumnType.STRING, false, "17"),
    new DataTableColumn("actions", $localize`Actions`, ColumnType.LINK, false, "20"),
  );

  applyItemsCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn("installType", $localize`Install Type`, ColumnType.STRING, false, "10"),
    new DataTableColumn("type", $localize`Type`, ColumnType.STRING, false, "10"),
    new DataTableColumn("name", $localize`Name`, ColumnType.STRING, false, "20"),
    new DataTableColumn("size", $localize`Size`, ColumnType.STRING, false, "10"),
    new DataTableColumn("description", $localize`Description`, ColumnType.STRING, false, "50"),
  );
  applyItemsRows: DataTableRow[] = [];
  isApplyItemsReady: boolean = false;
  isInit: boolean = false;
  currentUserAuthInfo: UserInfo;
  numAvailableInstances: number = 0;
  numCompatiblePlatforms: number = 0;
  showNoCompatiblePlatformError: boolean = false;
  syncCapabilityError: boolean = false;

  //subscription
  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;
  dataSubscription: Subscription;

  preFlightRequest: PreFlightRequest;
  upgradePromptResponse: UpgradePromptResponse;

  isAcknowledgedSkipMissing: boolean = false;
  patchesToBeReapplied: any[] = [];
  missingPatchesToInstall: number = 0;
  missingPatchesToReapply: number = 0;
  missingUpdate: boolean = false;

  isSendingPatchInstall: boolean = false;

  constructor(
    protected agentService: AgentService,
    protected eventService: EventService,
    protected router: Router,
    protected dataService: DataService,
    protected route: ActivatedRoute,
    protected modal: Modal,
    protected wsService: WebsocketService,
    protected authService: AuthenticationService,
    protected solutionSrv: SolutionService,
    protected activityService: ActivityService,
    protected softwareService: SoftwareInventoryService,
    protected fileSize: FileSizePipe,
    private cookieSrv: CookieService) {
    super(eventService, modal, authService, solutionSrv);
  }

  ngOnInit(): void {
    this.isInit = true;
    this.currentUserAuthInfo = this.authService.getLoggedInUser();

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.SyncCapabilities || msg.eventType == Events.ActivityCapabilities) {
        if (this.isPendingTransaction(msg.headers.transactionId, true)) {
          this.processSyncCapabilities(msg);
        }
      } else if (msg.eventType == Events.QuickAnalysis) {
        if (this.isPendingTransaction(msg.headers.transactionId, true)) {
          this.processQuickAnalysis(msg);
        }
      } else if (msg.eventType == Events.AnalyzeProduct) {
        if (this.isPendingTransaction(msg.headers.transactionId, true)) {
          this.processProductAnalysis(msg);
        }
      } else if (msg.eventType == Events.DownloadCompleted) {
        this.processDownloadComplete(msg);
      } else if (msg.eventType == Events.CSPreFlight || msg.eventType == Events.CSPreFlightProgress) {
        if (this.isPendingTransaction(msg.headers.transactionId, false)) {
          this.processPreFlightCheck(msg);
        }
      } else if (msg.eventType == Events.CSRegisteredServers) {
        if (this.isPendingTransaction(msg.headers.transactionId, true)) {
          this.processRegisteredServers(msg);
        }
      } else if (msg.eventType == Events.CSRemoveRegisteredInstance) {
        this.processRemoveRegisteredServer(msg);
      }
    });

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      if (this.isInit) {
        this.isInit = false;
        this.getSyncCapabilities();
      }
    });

    this.dataSubscription = this.dataService.getData().subscribe((data: any) => {
      if (data && data.type == ProductDataType.Transaction) {
        this.addTransactionId(data.id);
      } else if (data && data.type == ProductDataType.DownloadStarted) {
        this.setDownloadInProgress();
      }
    });
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.eventMessageSubscription.unsubscribe();
    this.dataSubscription.unsubscribe();
  }

  next(): void {
    this.isError = false;
    if (this.state == SyncSolutionState.SOURCE) {
      this.goToType();
    } else if (this.state == SyncSolutionState.TYPE) {
      this.goToTarget();
    } else if (this.state == SyncSolutionState.TARGET) {
      this.goToAnalyze();
    } else if (this.state == SyncSolutionState.ANALYZE) {
      this.goToDownload();
    } else if (this.state == SyncSolutionState.DOWNLOAD) {
      if (this.isAcknowledgedSkipMissing) {
        this.showSkipWarningDialog();
      } else {
        this.goToValidate();
      }
    } else if (this.state == SyncSolutionState.VALIDATE) {
      this.goToSync();
    }
  }

  back(): void {
    this.isError = false;
    if (this.state == SyncSolutionState.TYPE) {
      this.goToSource();
    } else if (this.state == SyncSolutionState.TARGET) {
      this.goToType();
    } else if (this.state == SyncSolutionState.ANALYZE) {
      this.goToTarget();
    } else if (this.state == SyncSolutionState.DOWNLOAD) {
      this.goToAnalyze();
    } else if (this.state == SyncSolutionState.VALIDATE) {
      this.goToDownload();
    } else if (this.state == SyncSolutionState.SYNC) {
      this.goToValidate();
    }
  }

  goToSource(): void {
    this.index = 0;
    this.state = SyncSolutionState.SOURCE;
  }

  goToType(): void {
    this.showSyncTypes();
    this.index = 1;
    this.state = SyncSolutionState.TYPE;
  }

  goToTarget(): void {
    this.showTargetEnvs();
    this.index = 2;
    this.state = SyncSolutionState.TARGET;
  }

  goToAnalyze(): void {
    this.analyze();
    this.index = 3;
    this.state = SyncSolutionState.ANALYZE;
  }

  goToDownload(): void {
    this.validatePackages();
    this.index = 4;
    this.state = SyncSolutionState.DOWNLOAD;
  }

  goToValidate(): void {
    this.validate();
    this.index = 5;
    this.state = SyncSolutionState.VALIDATE;
  }

  goToSync(): void {
    this.setSummary();
    this.activeTab = SyncTab.INSTANCE;
    this.index = 6;
    this.state = SyncSolutionState.SYNC;
  }

  goToActivities(): void {
    this.router.navigate(["/activities/overview"]);
  }

  cancel(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  onSelectedTabChange(selectedIndex: number) {
    this.activeTab = selectedIndex;
  }

  onSourceSolSelected(item: SelectOption): void {
    this.selectedSourceEnv = item;
    this.showSupportedProducts();
  }

  onProductSelected(item: SelectOption): void {
    this.selectedProduct = item.value.value;
    this.capabilities = this.selectedProduct.capabilities[0];
    this.showSyncVersions();
  }

  onVersionSelected(item: SelectOption): void {
    this.getSyncCapabilities(item.text);
    this.selectedVersion = item.value;
  }

  onTypeSelected(item: SelectOption): void {
    this.selectedType = item;
  }

  onTargetSelected(item: SelectOption): void {
    this.selectedTargetEnv = item;
    this.numAvailableInstances = 0;
    this.numCompatiblePlatforms = 0;
    this.showNoCompatiblePlatformError = false;
    this.showInstances();
  }

  getSolutions(): void {
    let enabledList: SelectOption[] = [];
    let disabledList: SelectOption[] = [];
    this.solutionSrv.getSolutions().pipe(
      tap(resp => {
        this.sourceEnvs = [];
        resp.body.forEach(sol => {
          let environments: any[] = sol.environments;
          environments.forEach(env => {
            env.disabled = env.agents.length < 1;
            if (env.disabled) {
              env.disabledType = $localize`No registered agents`;
            } else {
              let isProductEnabled: boolean = false;
              let productCanSync: boolean = false;
              for (let i = 0; i < env.agents.length; i++) {
                for (let j = 0; j < env.agents[i].products.length; j++) {
                  if (env.agents[i].products[j].isEnabled) {
                    isProductEnabled = true;
                  }
                  if (env.agents[i].products[j].canSync) {
                    productCanSync = true;
                  }

                  if (isProductEnabled && productCanSync) {
                    break;
                  }
                }
                if (isProductEnabled && productCanSync) {
                  break;
                }
              }

              env.disabled = !isProductEnabled || !productCanSync;
              env.disabledType = (!isProductEnabled || !productCanSync) ? $localize`No products to synchronize` : undefined;
            }
            let name = env.solName + ' : ' + env.name;
            if (env.disabled) {
              name += " (" + env.disabledType + ")";
            }
            if (env.disabled) {
              disabledList.push(new SelectOption(env, name, false, env.disabled));
            } else {
              enabledList.push(new SelectOption(env, name, false, env.disabled));
            }
          });
        });

        this.sourceEnvs = this.sourceEnvs.concat(enabledList.sort((a, b) => (a.text < b.text ? -1 : 1)));
        this.sourceEnvs = this.sourceEnvs.concat(disabledList.sort((a, b) => (a.text < b.text ? -1 : 1)));

      }),
      finalize(() => {
      })
    ).subscribe();
  }

  processSyncCapabilities(msg: MsgPayLoad): void {
    let msgBody = JSON.parse(msg.body.msg)
    if (msg.body.ok) {
      this.productCapabilities = msgBody;
      this.productCapabilities.forEach(capability => {
        let supportedProduct: any = {};
        supportedProduct.displayName = capability.productName;
        supportedProduct.value = capability;
        supportedProduct.disabled = true;
        let supported: any[] = (this.supportedProducts.filter((product) => {
          return (product.value.productName == supportedProduct.value.productName);
        }));
        if (supported == undefined || supported.length == 0) {
          this.supportedProducts.push(supportedProduct);
        }
      });
      this.getSolutions();
    } else {
      let errorMsg: string = '';
      errorMsg = (msgBody.error != undefined && msgBody.error == 'CAPABILITIES_AGENTS_OFFLINE') ?
        $localize`The system was unable to retrieve the list of required tasks. Please make sure the required agents are online and try again.` :
        $localize`An unknown error has occurred while trying to retrieve the tasks required to complete the selected activity.`;
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Error`,
            type: ModalMessageType.Error,
            message: errorMsg,
            primaryBtn: $localize`Close`,
            width: '550px'
          },
          id: 'otsc-sync-error-msg'
        }).afterClosed.subscribe(() => {
          this.cancel();
          sub.unsubscribe();
        });
    }
  }

  showSupportedProducts(): void {
    this.sourceAgents = [];
    let enabledProducts: any[] = [];
    let masterSystemIndex: number = -1;

    this.sourceProducts = [];
    this.supportedProducts.forEach(supportedProduct => {
      this.sourceProducts.push(new SelectOption(supportedProduct, supportedProduct.displayName, false, false));
    });

    if (this.selectedSourceEnv && this.selectedSourceEnv.value.agents) {
      this.selectedSourceEnv.value.agents.forEach((agent) => {
        let containsEnabledProduct: boolean = false;
        if (agent.products) {
          for(let i = 0; i < agent.products.length; i++){
            let product = agent.products[i];
            if (product.isEnabled && product.canSync) {
              product.agent = agent;
              let tmpIndex = enabledProducts.push(product) - 1;
              containsEnabledProduct = true;
              if (product.isMasterSystem) {
                masterSystemIndex = tmpIndex;
              }
            }

            if (containsEnabledProduct) {
              this.sourceAgents.push(agent.macAddress);
            }
          }
        }
      });

      this.sourceProduct = (masterSystemIndex > -1) ? enabledProducts[masterSystemIndex] : enabledProducts[0];
      this.sourceProduct.environment = this.selectedSourceEnv.value;
    }

    this.supportedVersions = [];

    enabledProducts.forEach(enabledProduct => {
      this.supportedProducts.forEach(supportedProduct => {
        if (supportedProduct.value.productIDs.indexOf(enabledProduct.productId) > -1) {
          supportedProduct.disabled = false;

          let existingVersion: any[] = this.supportedVersions.filter(version => version.productId == enabledProduct.productId && version.productVersion == enabledProduct.productVersion);
          if (!existingVersion || existingVersion.length == 0) {
            if (enabledProduct.productId == "OTCONTENTSERVER") {
              if (enabledProduct.isMasterSystem) {
                this.supportedVersions.push(enabledProduct);
              }
            } else {
              this.supportedVersions.push(enabledProduct);
            }
          }
        }
      });
    });

    if (this.supportedProducts.length == 1 && !this.supportedProducts[0].disabled) {
      this.selectedProduct = this.supportedProducts[0].value;
      this.sourceProducts[0].selected = true;
      this.showSyncVersions();
    }

  }

  showSyncVersions(): void {
    this.selectedVersion = undefined;
    this.sourceVersions = [];

    if (this.selectedProduct) {
      for (var i = 0; i < this.supportedVersions.length; i++) {
        this.supportedVersions[i].isFound = this.selectedProduct.productIDs.indexOf(this.supportedVersions[i].productId) > -1;
      }

      this.supportedVersions.forEach(supportedVersion => {
        this.sourceVersions.push(new SelectOption(supportedVersion, supportedVersion.productVersion, false, false));
      });

      if (this.supportedVersions.length == 1) {
        this.sourceVersions[0].selected = true;
        this.selectedVersion = this.supportedVersions[0].value;
      }
    }
  }

  showSyncTypes(): void {
    this.selectedType = undefined;
    this.supportedTypeOptions = [];

    if (this.selectedVersion) {
      if (this.selectedProduct && this.selectedProduct.capabilities) {
        //get supported sync types from sync capabilities
        this.selectedProduct.capabilities.forEach(capability => {
          let supportedType: any = {};
          supportedType.displayName = this.getTypeText(capability.activityType);
          supportedType.desc = this.getTypeText(capability.activityTypeDescKey);
          supportedType.value = capability.activityType;
          if (this.selectedType !== undefined && this.selectedType.value.displayName == supportedType.displayName) {
            this.supportedTypeOptions.push(new SelectOption(supportedType, supportedType.displayName, true, false));
          } else {
            if (supportedType.displayName !== '') {
              this.supportedTypeOptions.push(new SelectOption(supportedType, supportedType.displayName, false, false));
            }
          }
        });
      }

      //auto select type if there's only supported type
      if (this.supportedTypeOptions.length == 1) {
        this.supportedTypeOptions[0].selected = true;
        this.selectedType = this.supportedTypeOptions[0];
      }
    }
  }

  getTypeText(value: string): string {
    let text: string = '';
    switch (value) {
      case 'SYNC_PATCH_INSTALLATION':
        text = $localize`Synchronize Patch Installation`;
        break;
      case 'SYNC_PATCH_INSTALLATION_DESC':
        text = $localize`Installs any missing updates, patches, and language packs.`;
        break;
    }
    return text;
  }

  showTargetEnvs(): void {
    this.targetEnvs = [];
    let enabledList: SelectOption[] = [];
    let disabledList: SelectOption[] = [];

    this.sourceEnvs.forEach(env => {
      let envtmp: any = env.value;

      if (envtmp.disabled && envtmp.agents.length == 0) {
        envtmp.disabledType = $localize`No registered agents`;
      } else if (!envtmp.disabled && env.text == this.selectedSourceEnv.text) {
        //disable the env if it was selected as source
        envtmp.disabled = true;
      } else {
        let isProductEnabled: boolean = false;
        for (let i = 0; i < envtmp.agents.length; i++) {
          for (let j = 0; j < envtmp.agents[i].products.length; j++) {
            if (envtmp.agents[i].products[j].isEnabled) {
              isProductEnabled = true;
              break;
            }
          }
          if (isProductEnabled) {
            break;
          }
        }

        envtmp.disabled = !isProductEnabled;
        envtmp.disabledType = !isProductEnabled ? $localize`No enabled products` : undefined;
      }


      let name = envtmp.solName + ' : ' + envtmp.name;
      if (envtmp.disabled && envtmp.disabledType !== undefined) {
        name += " (" + envtmp.disabledType + ")";
      }

      if (envtmp.disabled) {
        disabledList.push(new SelectOption(envtmp, name, false, envtmp.disabled));
      } else {
        if (this.selectedTargetEnv !== undefined && this.selectedTargetEnv.value.solName == envtmp.solName &&
          this.selectedTargetEnv.value.name == envtmp.name) {
          enabledList.push(new SelectOption(envtmp, name, true, envtmp.disabled));
          this.showInstances();
        } else {
          enabledList.push(new SelectOption(envtmp, name, false, envtmp.disabled));
        }
      }
    });

    this.targetEnvs = this.targetEnvs.concat(enabledList.sort((a, b) => (a.text < b.text ? -1 : 1)));
    this.targetEnvs = this.targetEnvs.concat(disabledList.sort((a, b) => (a.text < b.text ? -1 : 1)));

    if (this.selectedType) {
      for (var i = 0; i < this.selectedProduct.capabilities.length; i++) {
        if (this.selectedProduct.capabilities[i].activityType == this.selectedType.value.value) {
          this.capabilities = this.selectedProduct.capabilities[i];
          break;
        }
      }
    }
  }

  showInstances(): void {
    this.targetAgents = [];
    this.instanceRows = [];
    this.componentInstances = [];
    this.enabledCompInstanceCounter = 0;
    if (this.selectedType && this.selectedSourceEnv.value.agents) {
      this.selectedTargetEnv.value.agents.forEach(agent => {
        let containsEnabledProduct: boolean = false;
        if (agent.products) {
          agent.products.forEach(product => {
            //make sure product ID is supported
            if (this.selectedProduct.productIDs.indexOf(product.productId) > -1) {
              //filter based on selected version
              if (product.productVersion.match(this.selectedVersion.versionMatchRegex)) {
                if(this.selectedTargetProduct == undefined) {
                  this.selectedTargetProduct = product;
                }
                this.numAvailableInstances++;
                if (product.platform == this.selectedVersion.platform) {
                  this.numCompatiblePlatforms++;
                  let compInstance: any = {};
                  compInstance.productName = product.productName;
                  compInstance.productVersion = product.productVersion;
                  compInstance.system = agent.systemName;
                  compInstance.installationDirectory = product.installationDirectory;
                  compInstance.isEnabled = new SupportedStatusExt(product.isEnabled,
                    product.isEnabled ? 'otsc-icon-status-available' : 'otsc-icon-status-busy',
                    product.isEnabled ? $localize`Enabled` : $localize`Not enabled`);
                  //make sure platform is the same
                  compInstance.canBeSynced = product.isEnabled && (product.platform == this.selectedVersion.platform);
                  compInstance.productId = product.productId;
                  compInstance.value = product;
                  compInstance.value.agent = agent;
                  compInstance.macAddress = agent.macAddress;
                  compInstance.platform = product.platform;
                  this.componentInstances.push(compInstance);
                  let tmpRow = new DataTableRow(compInstance, [], false, compInstance.canBeSynced);
                  tmpRow.disableCheckbox = true;
                  this.instanceRows.push(tmpRow);

                  if (compInstance.canBeSynced) {
                    this.enabledCompInstanceCounter++;
                    containsEnabledProduct = true;
                  }
                }
              }
            }
          });
        }

        if (containsEnabledProduct) {
          this.targetAgents.push(agent.macAddress);
        }
      });
    }

    if (this.numAvailableInstances > 0) {
      this.showNoCompatiblePlatformError = (this.numCompatiblePlatforms > 0) ? false : true;
    }
  }

  analyze(): void {
    this.isSynced = false;
    this.result = undefined;
    this.isError = false;
    this.errorMessage = "";
    this.isAnalysisError = false;
    this.isAnalysisSuccess = false;
    this.additionalModuleWarning = false;
    this.additionalPatchWarning = false;
    this.unknownPatchWarning = false;
    this.customLangPackWarning = false;
    this.needsUpdate = false;
    this.canUpdate = false;
    this.canSkipWarnings = false;
    this.analysisResultList = [];
    this.progressText = $localize`Validating agent status...`;
    this.analyzing = true;

    let agents = this.sourceAgents.concat(this.targetAgents);
    this.activityService.isAgentsOnline(agents).pipe(
      tap((resp: any) => {
        if (resp.body == 0) {
          this.isTargetSystemBusy();
        } else {
          this.isError = true;
          this.errorMessage = $localize`One or more agents from source or target environment/solution are offline. Please make sure agents are online and try again.`;
          this.analyzing = false;
        }
      })).subscribe();
  }

  isTargetSystemBusy(): void {
    this.activityService.isTargetSystemsBusy(this.targetAgents).pipe(
      tap((resp: any) => {
        var isBusy = JSON.parse(resp.body);
        if (isBusy) {
          this.isError = true;
          this.errorMessage = $localize`One or more agents from target environment/solution are busy. Please try again later.`;
          this.analyzing = false;
        } else {
          for (let i = 0; i < this.componentInstances.length; i++) {
            //supports master system
            if (this.selectedVersion.isMasterSystem) {
              if (this.componentInstances[i].value.isMasterSystem && this.componentInstances[i].isEnabled &&
                this.componentInstances[i].value.productVersion.match(this.selectedVersion.versionMatchRegex)) {
                this.targetProduct = this.componentInstances[i].value;
                break;
              }
            } else if (this.componentInstances[i].isEnabled) {
              this.targetProduct = this.componentInstances[i].value;
              break;
            }
          }
          this.targetProduct.environment = this.selectedTargetEnv.value;
          this.startAnalysisTargetProduct();
        }
      })).subscribe();
  }

  startAnalysisTargetProduct(): void {
    this.masterSystem = undefined;
    let discoveredProductGroup: DiscoveredProduct[];
    this.progressText = $localize`Analyzing a product from source environment/solution...`;
    this.softwareService.getDiscoveredProducts(this.sourceProduct.environment.id).pipe(
      tap((resp: any) => {
        discoveredProductGroup = resp.body;
        discoveredProductGroup.forEach(productGroup => {
          productGroup.products.forEach(product => {
            Object.keys(product.instances).forEach(key => {
              let instances: any = product.instances[key];
              if (this.masterSystem === undefined && product.instances.hasOwnProperty(key)) {
                let masterSystemResult: any[] = instances.filter(instance => instance.isMasterInstance && instance.version == this.selectedVersion.productVersion);
                if (masterSystemResult.length > 0 && masterSystemResult[0].setupId == this.sourceProduct.productId
                  && masterSystemResult[0].installPath == this.sourceProduct.installationDirectory) {
                  this.masterSystem = masterSystemResult[0];
                  this.masterSystem.updateLevel = parseInt(product.patchLevel);
                  this.masterSystem.updateINILevel = parseInt(product.patchLevel);
                  this.masterSystem.updateName;
                  this.masterSystem.productGroupId = productGroup.id;
                  this.selectedProduct.isSupportHotfix = product.supportHotfix;
                }
              }
            });
          });
        });

        //master system was not found
        if (this.masterSystem === undefined) {
          //analyze
          this.softwareService.sendProductAnalysis(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
            new ProductAnalysisRequest(this.targetProduct.plugin, this.targetProduct.productId, this.targetProduct.productVersion,
              this.targetProduct.installationDirectory, this.targetProduct.agent.macAddress, undefined, ProductAnalysisType.SYNC_PATCH_INSTALLATION,
              this.targetProduct.productManifestId, this.sourceProduct.environment.id)).pipe(
                tap(resp => {
                }),
                catchError(resp => {
                  this.isError = true;
                  this.errorMessage = TranslationUtils.getTaskErrorMessage("PRODUCT_ANALYSIS", this.targetProduct.agent.system);
                  this.analyzing = false;
                  return of(resp);
                })).subscribe();
        } else {
          //get products from target environment/solution
          this.sourceMasterSystem = this.masterSystem;
          this.softwareService.getDiscoveredProducts(this.targetProduct.environment.id).pipe(
            tap(resp => {
              let discoveredTargetProductGroup: any = resp.body;
              discoveredTargetProductGroup.forEach((targetProductGroup: any) => {
                targetProductGroup.products.forEach(product => {
                  if (product.groupId == this.targetProduct.productGroupId) {
                    Object.keys(product.instances).forEach(key => {
                      let instances: any = product.instances[key];
                      let masterSystemResult: any[] = instances.filter(instance => instance.isMasterInstance);
                      if (masterSystemResult.length > 0) {
                        this.masterSystem = masterSystemResult[0];
                      }
                    });

                    this.targetProductInstances = product.instances;
                    this.targetProduct.supportsHotfix = product.supportHotfix;
                    //perform quick analysis on the product from source environment/solution
                    this.runningQuickAnalysis = true;
                    this.softwareService.sendQuickAnalysis(this.wsService.getClientId(), this.wsService.getWSEnvId(),
                      this.addTransaction(), this.sourceMasterSystem).pipe(
                        tap(resp => {
                        }),
                        catchError(resp => {
                          this.isError = true;
                          this.errorMessage = TranslationUtils.getTaskErrorMessage("QUICK_ANALYSIS", this.masterSystem.system);
                          this.analyzing = false;
                          return of(resp);
                        }),).subscribe();
                  }
                });
              });
            })).subscribe();
        }
      })).subscribe();
  }

  processQuickAnalysis(msg: any): void {
    let quickAnalysisResult = JSON.parse(msg.body.msg);

    if (quickAnalysisResult.isValid) {

      //received the source product master system information
      if (this.sourceMasterSystem && this.sourceMasterSystem.productGroupId == quickAnalysisResult.productGroupId &&
        quickAnalysisResult.macAddress == this.sourceProduct.agent.macAddress) {
        this.sourceMasterSystem.langs = quickAnalysisResult.langCodes;
        this.sourceMasterSystem.modules = quickAnalysisResult.modules; //modules from the source
        this.sourceMasterSystem.patches = quickAnalysisResult.patches; //patches from the source
        this.sourceMasterSystem.isReady = true;
      }

      //analyze the target product against the source product master system
      this.progressText = $localize`Analyzing a product from target environment/solution...`;
      this.softwareService.sendProductAnalysis(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
        new ProductAnalysisRequest(this.targetProduct.plugin, this.targetProduct.productId, this.targetProduct.productVersion,
          this.targetProduct.installationDirectory, this.targetProduct.agent.macAddress, this.sourceMasterSystem, ProductAnalysisType.SYNC_PATCH_INSTALLATION,
          this.targetProduct.productManifestId)).pipe(
            tap(resp => {
            }),
            catchError(resp => {
              this.isError = true;
              this.errorMessage = TranslationUtils.getTaskErrorMessage("PRODUCT_ANALYSIS", this.masterSystem.system);
              this.analyzing = false;
              return of(resp);
            })).subscribe();
    } else {
      this.result = new Object();
      this.isAnalysisError = true;
      if (this.sourceProduct !== undefined) {
        //check to see if solution switch is required
        this.result.srcEnv = this.sourceProduct.environment.id != this.solutionInfo.currentEnvironment.id ? this.sourceProduct.environment : undefined;
      }
      this.analyzing = false;
      this.isError = true;
      this.errorMessage = $localize`The primary system is out of date. An analysis of the primary system must be completed before continuing.`;
    }

    this.runningQuickAnalysis = false;
  }

  processProductAnalysis(msg: any): void {

    var analysisResult = JSON.parse(msg.body.msg);

    this.needsUpdate = analysisResult.needsUpdate;
    this.canUpdate = analysisResult.canUpdate;
    this.canSkipWarnings = analysisResult.canSkipWarnings;
    this.currentVersion = analysisResult.currentVersion;
    this.requiredUpdateName = analysisResult.requiredUpdateName;
    this.requiredPatchLevel = analysisResult.requiredPatchLevel;
    this.requiredHotfixes = (analysisResult.requiredHotfixes != undefined) ? analysisResult.requiredHotfixes : [];
    this.patchesToBeRemoved = (analysisResult.toBeRemovedHotfixes != undefined) ? analysisResult.toBeRemovedHotfixes : [];
    this.customLangPacks = (analysisResult.customLangPacks != undefined) ? analysisResult.customLangPacks : [];
    this.analysisStatus = analysisResult.status;
    this.messageMap = this.analysisStatus.translationMessageMap;
    this.messageCount = 0;
    let customMissing = (analysisResult.missingCustomLangCodes != undefined) ? analysisResult.missingCustomLangCodes : [];
    this.requiredLangs = [];
    this.patchesToBeReapplied = analysisResult.requiredReapplyHotfixes;

    this.patchesToBeReapplied.forEach(patch => {
      patch.details = new Object();
      patch.details.size = patch.size;
    });

    if (analysisResult.requiredLangs != undefined && analysisResult.requiredLangs.length > 0) {
      analysisResult.requiredLangs.forEach(lang => {
        let custRows: string[] = this.customLangPacks.filter((custLang: string) => {
          return custLang == lang;
        });
        let custMissingRows: string[] = customMissing.filter((custLang: string) => {
          return custLang == lang;
        });

        if (custRows.length == 0 && custMissingRows.length == 0) {
          this.requiredLangs.push(lang);
        }
      });
    }

    if (this.messageMap != undefined) {
      this.messageCount = Object.keys(this.messageMap).length;

      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_UPDATE_ERROR")) {
        if (this.messageMap.SYSTEM_ANALYSIS_UPDATE_ERROR.actualUpdate == undefined ||
          this.messageMap.SYSTEM_ANALYSIS_UPDATE_ERROR.actualUpdate == '') {
          this.canUpdate = false;
          this.canSkipWarnings = false;
          this.messageMap.BRING_UP_TO_DATE_ACTUAL_UPDATE_ERROR = {};
          delete this.messageMap.SYSTEM_ANALYSIS_UPDATE_ERROR;
        }
      }
      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_MODULE_MISSING_ERROR_LIST") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_MISSING_ERROR_LIST")) {
        this.missingModuleWarning = true;
      }

      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_MODULE_ADDITIONAL_ERROR_LIST") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_ADDITIONAL_ERROR_LIST")) {
        this.additionalModuleWarning = true;
      }

      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_PATCH_ADDITIONAL_ERROR") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_PATCH_ADDITIONAL_ERROR")) {
        this.additionalPatchWarning = true;
      }

      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_CUSTOM_LANG_ERROR") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_CUSTOM_LANG_ERROR") ||
        this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_MISSING_CUSTOM_LANG_ERROR") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MISSING_CUSTOM_LANG_ERROR")) {
        this.customLangPackWarning = true;
      }

      if (this.messageMap.hasOwnProperty("SYSTEM_ANALYSIS_UNKNOWN_PATCH_ERROR") ||
        this.messageMap.hasOwnProperty("SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_UNKNOWN_PATCH_ERROR")) {
        this.unknownPatchWarning = true;
      }
    }
    if (this.needsUpdate) {
      if (this.canUpdate) {
        this.isError = false;
        this.isAnalysisSuccess = true;
        this.analyzing = false;
      } else {
        //can't update
        this.errorMessage = this.canSkipWarnings ?
          $localize`The product from target environment/solution needs to be updated, but some problems have been identified. The system is unable to automatically correct the problems. You can correct the problems before System Center Manager synchronizes the product, or afterwards.` :
          $localize`The system has encountered the following errors and can't be synchronzied at this time.`;
        this.isError = true;
        this.analyzing = false;
      }
      this.processMessageMap();
    } else {
      //products from both source and target environment/solutions are already synced
      this.isSynced = true;
      this.analyzing = false;
    }

  }

  processMessageMap(): void {
    let errorImg: string = './assets/icons/formfield_error.svg';
    let notificationImg: string = './assets/icons/status_outdated24.svg';
    let tmpAnalysisList: ListItem[] = [];

    Object.keys(this.messageMap).forEach(key => {
      let tmpItem: ListItem = new ListItem();
      let message = this.messageMap[key];
      switch (key) {
        case "SYSTEM_ANALYSIS_PATCH_MISSING_ERROR":
          tmpItem.msg = $localize`This System does not have the same patches as the Primary System. It has the following missing patches: ${message.patch}`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_HOTFIX_ACTIVE":
          tmpItem.msg = $localize`The required hotfix ${message.hotfix} is currently being downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_HOTFIX_ERROR":
          tmpItem.msg = $localize`The required hotfix ${message.hotfix} encountered an error during download.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "VALIDATE_DOWNLOAD_HOTFIX_ERROR_MULTI":
          tmpItem.msg = $localize`The required hotfixes ${message.hotfix} all encountered an error during download.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "VALIDATE_DOWNLOAD_HOTFIX_MISSING":
          tmpItem.msg = $localize`The required hotfix ${message.hotfix} has not been downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_HOTFIX_MISSING_MULTI":
          tmpItem.msg = $localize`The required hotfixes ${message.hotfix} have not been downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_ACTIVE":
          tmpItem.msg = $localize`The required language pack ${message.lang} is currently being downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_ACTIVE_MULTI":
          tmpItem.msg = $localize`The required language packs ${message.lang} are currently being downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_ERROR":
          tmpItem.msg = $localize`The required language pack ${message.lang} encountered an error during download.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_ERROR_MULTI":
          tmpItem.msg = $localize`The required language packs ${message.lang} all encountered an error during download.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_MISSING":
          tmpItem.msg = $localize`The required language pack ${message.lang} has not been downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_LANG_MISSING_MULTI":
          tmpItem.msg = $localize`The required language packs ${message.lang} have not been downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_UPDATE_ACTIVE":
          tmpItem.msg = $localize`The required update ${message.update} is currently being downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "VALIDATE_DOWNLOAD_UPDATE_ERROR":
          tmpItem.msg = $localize`The required update ${message.update} encountered an error during download.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "VALIDATE_DOWNLOAD_UPDATE_MISSING":
          tmpItem.msg = $localize`The required update ${message.update} has not been downloaded.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_MISSING_ERROR_LIST":
          tmpItem.msg = $localize`This Primary System from target environment/solution does not have the same modules as the Primary System from source environment/solution. It has ${message.numModule} missing module(s):  <br> ${message.list}`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_CUSTOM_LANG_ERROR":
          tmpItem.msg = $localize`This Primary System from target environment/solution has custom language packs installed. It has the following custom language packs: ${message.langs}.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MISSING_CUSTOM_LANG_ERROR":
          tmpItem.msg = $localize`This Primary System from source environment/solution has custom language packs installed. It has the following custom language packs: ${message.langs}.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_ADDITIONAL_ERROR":
          tmpItem.msg = $localize`This Primary System from target environment/solution does not have the same modules as the Primary System from source environment/solution. It has ${message.numModule} additional module(s).`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_ADDITIONAL_ERROR_LIST":
          tmpItem.msg = $localize`This Primary System from target environment/solution does not have the same modules as the Primary System from source environment/solution. It has ${message.numModule} additional module(s): <br> ${message.list}`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_MODULE_MISSING_ERROR":
          tmpItem.msg = $localize`This Primary System from target environment/solution does not have the same modules as the Primary System from source environment/solution. It has ${message.numModule} missing module(s).`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_PATCH_ADDITIONAL_ERROR":
          tmpItem.msg = $localize`This Primary System from target environment/solution does not have the same patches as the Primary System from source environment/solution. It has the following additional patches: ${message.patch}.`;
          tmpItem.imgSrc = notificationImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_UPDATE_GREATER_THAN_ERROR":
          tmpItem.msg = $localize`The Primary System from target environment/solution is currently at a newer version ${message.actualUpdate} while the selected product from source environment/solution is currently at ${message.expectedUpdate}.`;
          tmpItem.imgSrc = errorImg;
          break;
        case "SYNC_PATCH_INSTALL_SYSTEM_ANALYSIS_UNKNOWN_PATCH_ERROR":
          tmpItem.msg = $localize`The following unknown patches were found during the analysis: ${message.patch}.`;
          tmpItem.imgSrc = notificationImg;
          break;
      }

      tmpAnalysisList.push(tmpItem);
    });

    this.analysisResultList = tmpAnalysisList;
  }

  validatePackages(): void {
    this.progressText = $localize`Validating required packages...`;
    this.patchesToInstall = [];
    this.langPacksToInstall = [];
    this.packageRows = [];
    this.needsDownload = false;
    this.isPackageError = false;
    this.isPackageDownloadActive = false;

    let tmpRows: DataTableRow[] = [];
    this.isOnlineMode = this.authService.getLoggedInUser().settings.isOnlineMode;
    if (!this.isOnlineMode) {
      this.packageCols = Array<DataTableColumn>(
        new DataTableColumn('displayType', $localize`Type`, ColumnType.STRING, true, "25"),
        new DataTableColumn('name', $localize`Name`, ColumnType.STRING, false, "20"),
        new DataTableColumn('action', $localize`Action`, ColumnType.STRING, false, "20"),
        new DataTableColumn('status', $localize`Status`, ColumnType.STATUSEXT, false, "25"),
        new DataTableColumn("url", $localize`URL`, ColumnType.DOWNLOADLINK, false, "10")
      );
    }

    if (this.requiredUpdateName != undefined && this.requiredUpdateName != '') {
      this.missingUpdate = true;
      tmpRows.push(new DataTableRow(new PackageRow(
        "missing", "update", $localize`Update`, this.requiredUpdateName, { className: 'otsc-icon-status-outdated', altText: $localize`Validating...` }, $localize`Install`, undefined), [], false, true, false, ""));
    }

    if (this.requiredHotfixes.length > 0) {
      this.requiredHotfixes.forEach(hotfix => {
        tmpRows.push(new DataTableRow(new PackageRow(
          "missing", "hotfixes", $localize`Patch`, hotfix, { className: 'otsc-icon-status-outdated', altText: $localize`Validating...` }, $localize`Install`, undefined), [], false, true, false, ""));
      });
    }

    if (this.patchesToBeReapplied.length > 0) {
      this.patchesToBeReapplied.forEach(hotfix => {
        tmpRows.push(new DataTableRow(new PackageRow(
          "missing", "hotfixes", $localize`Patch`, hotfix.name, { className: 'otsc-icon-status-outdated', altText: $localize`Validating...` }, $localize`Reapply`, undefined), [], false, true, false, ""));
      });
    }

    if (this.requiredLangs.length > 0) {
      this.requiredLangs.forEach(lang => {
        if (this.customLangPacks.indexOf(lang) == -1) {
          tmpRows.push(new DataTableRow(new PackageRow(
            "missing", "langs", $localize`Language pack`, lang, { className: 'otsc-icon-status-outdated', altText: $localize`Validating...` }, $localize`Install`, undefined), [], false, true, false, ""));
        }
      });
    }

    this.packageRows = tmpRows;
    this.isValidatingPackages = true;
    this.isDownloading = false;
    setTimeout(() => {
      let combinedPatchList = this.requiredHotfixes.length > 0 ? this.requiredHotfixes.slice() : [];
      if (this.patchesToBeReapplied.length > 0) {
        this.patchesToBeReapplied.forEach(patch => {
          combinedPatchList.push(patch.name);
        });
        combinedPatchList = combinedPatchList.sort((a, b) => (a < b ? 1 : -1));
      }
      this.softwareService.validatePackages(this.solutionInfo.currentEnvironment.id, this.targetProduct.agent.macAddress,
        this.targetProduct.installationDirectory, this.requiredUpdateName, this.requiredPatchLevel, this.requiredLangs,
        combinedPatchList).pipe(
          tap(resp => {
            this.missingPatchesToInstall = 0;
            this.missingPatchesToReapply = 0;
            this.patchesToInstall = [];
            this.langPacksToInstall = [];
            if (resp.body.dataJson !== undefined) {
              this.validationData = resp.body.dataJson;
              let tmpRows: DataTableRow[] = [];
              Object.keys(this.validationData).forEach(key => {
                if (key == 'downloaded' || key == 'missing') {
                  if (key == 'missing' && Object.keys(this.validationData[key]).length > 0) {
                    this.needsDownload = true;
                  }

                  Object.keys(this.validationData[key]).forEach(packageTypeKey => {
                    let packageList = this.validationData[key][packageTypeKey];
                    let displayType: string = "";
                    let name: string = "";
                    let objRows: DataTableRow[] = [];
                    let actionMenus: ActionMenuItem[];
                    if (packageTypeKey == 'update') {
                      displayType = $localize`Update`;
                      name = packageList.description;
                      packageList.status = (key == 'missing') ? 'AVAILABLE' : 'COMPLETE';
                      if (packageList.status == 'COMPLETE') {
                        this.missingUpdate = false;
                      }
                      this.updateToInstall = packageList;
                      actionMenus = [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, packageList, false, true)];
                      objRows = this.packageRows.filter((row: DataTableRow) => {
                        return row.data.type == 'update';
                      });
                      objRows[0].actions = actionMenus;
                      objRows[0].data.name = name;
                      objRows[0].data.status = Utils.getDownloadStatus(packageList);
                      objRows[0].data.url = packageList.details.downloadURL
                      objRows[0].data.packageData = packageList;
                    } else {
                      packageList.forEach(packageObj => {
                        if (packageTypeKey == 'langs') {
                          displayType = $localize`Language pack`;
                          name = packageObj.displayName;
                          this.langPacksToInstall.push(packageObj);
                          objRows = this.packageRows.filter((row: DataTableRow) => {
                            return row.data.type == packageTypeKey && row.data.name == packageObj.langCode;
                          });
                          objRows[0].data.url = packageObj.downloadURL;
                        } else {
                          displayType = $localize`Patch`;
                          name = packageObj.name;
                          objRows = this.packageRows.filter((row: DataTableRow) => {
                            return row.data.type == packageTypeKey && row.data.name == name;
                          });
                          if (objRows[0].data.action == 'Install') {
                            this.patchesToInstall.push(packageObj);
                          }
                          objRows[0].data.url = packageObj.details.downloadURL;
                        }

                        if (key == 'missing') {
                          if (objRows[0].data.action == 'Install') {
                            this.missingPatchesToInstall++;
                          } else {
                            this.missingPatchesToReapply++;
                          }
                        }

                        packageObj.status = (key == 'missing') ? 'AVAILABLE' : 'COMPLETE';
                        actionMenus = [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, packageObj, false, true)];
                        objRows[0].actions = actionMenus;
                        objRows[0].data.name = name;
                        objRows[0].data.status = Utils.getDownloadStatus(packageObj);
                        objRows[0].data.packageData = packageObj;
                      });
                    }
                  });
                }
              });

            }

            if (resp.body.success) {
              this.isPackageError = false;

              if (this.validationData.downloaded.update != undefined && (this.validationData.downloaded.langs != undefined && this.validationData.downloaded.langs.length > 0)) {
                this.validationData.downloaded.update.details.downloadedLangPackList = this.validationData.downloaded.langs;
                this.validationData.downloaded.update.details.downloadedLangPackList.forEach(lang => {
                  lang.displaySize = this.fileSize.transform(lang.size);
                });
              }

              this.validationData.toBeRemovedHotfixes = this.patchesToBeRemoved;
              this.validationData.toBeRemovedHotfixes.forEach(patch => {
                patch.details = new Object();
                patch.details.size = patch.size;
              });

              this.isValidatingPackages = false;
              this.isValidatingPackagesComplete = true;

            } else {
              this.isPackageError = true;
              this.isValidatingPackages = false;
              this.isValidatingPackagesComplete = true;
            }
          })).subscribe();
    }, 1000);
  }

  getDownloadCount(): number {
    let count: number = 0;
    this.packageRows.forEach(row => {
      if (!row.data.packageData?.isDownloaded) {
        count++;
      }
    });
    return count;
  }

  download(): void {
    let user: UserInfo = this.authService.getLoggedInUser();
    this.isOnlineMode = user.settings.isOnlineMode;
    this.isProxyRequired = user.settings.proxyPrompt;
    this.isAcknowledgedSkipMissing = false;
    this.itemsToDownload = new DownloadItem();

    this.packageRows.forEach(row => {
      if (!row.data.packageData.isDownloaded) {
        if (row.data.type == 'update') {
          this.itemsToDownload.updates.push(row.data.packageData);
        } else if (row.data.type == 'langs') {
          this.itemsToDownload.languages.push(row.data.packageData);
        } else {
          this.itemsToDownload.patches.push(row.data.packageData);
        }
      }
    });
    
    Utils.startDownload(this.isProxyRequired, this.selectedTargetProduct.productManifestId, this.selectedTargetProduct.productName,
      this.itemsToDownload, "", this.modal, this.dataService, this.softwareService);
  }

  private setDownloadInProgress(): void {

    this.isDownloading = true;

    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.packageData.status != "COMPLETE";
    });

    if (rows.length > 0) {
      rows.forEach(row => {
        row.data.packageData.status = 'DOWNLOAD';
        row.data.status = Utils.getDownloadStatus(row.data.packageData);
      });
    }

    Utils.showDownloadStartedDialog(DownloadType.HOTFIX, this.itemsToDownload.getTotalItems(), this.modal, this.cookieSrv);
    
  }

  processDownloadComplete(msg: MsgPayLoad): void {
    if (msg.body.langPackId > 0) {
      this.setLangDownloadComplete(msg.body.langPackId, msg.body.status);
    } else if (msg.body.hotfixId > 0) {
      this.setPatchDownloadComplete(msg.body.hotfixId, msg.body.status);
    } else if (msg.body.updateId > 0) {
      this.setUpdateDownloadComplete(msg.body.updateId, msg.body.status);
    }

    this.setDownloadingComplete();
  }

  private setUpdateDownloadComplete(id: number, status: string): void {
    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.type == "update" && row.data.packageData.id == id;
    });

    if (rows.length > 0) {
      rows[0].data.packageData.status = "COMPLETE";
      rows[0].data.status = Utils.getDownloadStatus(rows[0].data.packageData);
    }
  }

  private setLangDownloadComplete(id: number, status: string): void {
    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.type == "langs" && row.data.packageData.langpackDBId == id;
    });

    if (rows.length > 0) {
      rows[0].data.packageData.status = "COMPLETE";
      rows[0].data.status = Utils.getDownloadStatus(rows[0].data.packageData);
    }
  }

  private setPatchDownloadComplete(id: number, status: string): void {
    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.type == "hotfixes" && row.data.packageData?.id == id;
    });

    if (rows.length > 0) {
      rows[0].data.packageData.status = "COMPLETE";
      rows[0].data.status = Utils.getDownloadStatus(rows[0].data.packageData);
    }
  }

  private setDownloadingComplete(): void {
    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.packageData.status != "COMPLETE";
    });

    if (rows.length == 0) {
      this.validatePackages();
    }
  }

  downloadActionMenuClicked(event: ActionMenuEvent): void {
    let data = (event.data[0] instanceof PackageRow) ? event.data[0].packageData : event.data[0];
    if (event.name === "Details") {
      if (data.langCode) {
        this.modal.open(ObjectDetailsComponent, {
          data: { obj: data, type: ObjectDetailsTypes.LANG_PACK },
          id: 'otsc-enable-langpack-details-dialog'
        });
      } else {
        this.modal.open(ObjectDetailsComponent, {
          data: { obj: data, type: event.data[0].type == "update" ? ObjectDetailsTypes.UPDATE : ObjectDetailsTypes.PATCH },
          id: 'otsc-enable-patch-details-dialog'
        });
      }
    }
  }

  goToProducts(): void {
    if (this.result.srcEnv) {
      this.switchSolution(this.result.srcEnv).then((data) => {
        this.navigateToProducts();
      });
    } else {
      this.router.navigate(['/products/overview']);
    }
  }

  isNextDisabled(): boolean {
    let isDisabled: boolean = false;
    if (this.analyzing) {
      isDisabled = true;
    } else {
      if (this.needsUpdate) {
        if (this.canUpdate) {
          isDisabled = false;
        }

        if (!this.canUpdate) {
          if (this.canSkipWarnings) {
            if (this.missingModuleWarning && !this.skipMissingModules) {
              isDisabled = true;
            }

            if (this.additionalModuleWarning && !this.skipAdditionalModules) {
              isDisabled = true;
            }

            if (this.additionalPatchWarning && !this.skipAdditionalPatches) {
              isDisabled = true;
            }

            if (this.customLangPackWarning && !this.skipCustomLangs) {
              isDisabled = true;
            }

            if (this.unknownPatchWarning && !this.skipUnknownPatch) {
              isDisabled = true;
            }
          } else {
            isDisabled = true;
          }
        }
      }
    }
    return isDisabled;
  }

  validate(): void {
    this.isError = false;
    this.preReqCheckWaiting = true;
    this.preReqCheckInProcess = false;
    this.preReqCheckSuccess = false;
    this.preFlightInstanceRows = [];
    this.preReqRegisteredServerCheck.additionalHosts = [];
    this.preReqRegisteredServerCheck.missingHosts = [];
    this.openLogin();
  }

  openLogin(): void {
    let updateVersion: string = this.updateToInstall ? this.updateToInstall.name : this.currentVersion;
    var loginModal = this.modal.open(LoginCsComponent, {
      data: {
        title: $localize`Sign in`,
        instance: this.masterSystem,
        mode: 'sign_in',
        current: this.masterSystem.version,
        version: updateVersion,
        plugin: this.selectedProduct.pluginId
      },
      id: 'otsc-products-sign-in-dialog'
    }).afterClosed.subscribe((result: any) => {
      if (result) {
        this.csInfo = result;
        this.setupPreFlightCheck();
      } else {
        this.preReqCheckWaiting = false;
        this.preReqCheckSuccess = false;
        this.isError = true;
        this.errorMessage = $localize`You must login to synchronize the target system.`;
      }
      loginModal.unsubscribe();
    });
  }

  setupPreFlightCheck(): void {
    let selectedRows: DataTableRow[] = [];

    this.instanceRows.forEach(row => {
      if (row.selected) {
        selectedRows.push(row);
      }
    });

    for (let key in this.targetProductInstances) {
      let instances: ProductInstance[] = this.targetProductInstances[key];
      instances.forEach((instance: ProductInstance) => {
        let row: DataTableRow = new DataTableRow({
          system: instance.system,
          installPath: instance.installPath,
          version: this.selectedVersion.productVersion,
          platform: this.selectedVersion.platform,
          instance: instance,
          actions: ''
        }, [], false, true, false);

        row.disableCheckbox = true;
        this.preFlightInstanceRows.push(row);
      });
    }
    this.preReqCheckInProcess = true;
    this.preReqCheckWaiting = false;
    this.softwareService.sendRegisteredServers(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
      this.csInfo.token, this.csInfo.restUrl, this.targetProductInstances).pipe(
        tap(resp => {
        }),
        catchError(resp => {
          this.errorMessage = TranslationUtils.getTaskErrorMessage("REGISTERED_SERVERS", undefined);
          this.isError = true;
          this.preReqCheckInProcess = false;
          return of(resp);
        })).subscribe();
  }

  processRemoveRegisteredServer(msg: MsgPayLoad): void {
    let csRemoveRegInstanceStatus: TaskStatus = JSON.parse(msg.body.msg);

    if (csRemoveRegInstanceStatus.completed && csRemoveRegInstanceStatus.success) {
      let msg = new MsgPayLoad(undefined, {
        message: $localize`Systems were successfully unregistered.`
      });
      msg.setEvent(Events.SuccessMsg);
      Utils.addHeaderMessageBanner(this.eventService, msg);
      this.validate();
    } else {
      let msg = new MsgPayLoad(undefined, {
        message: $localize`Systems could not be unregistered. Please check Content Server log for details.`
      });
      msg.setEvent(Events.ErrorMsg);
      Utils.addHeaderMessageBanner(this.eventService, msg);
    }
  }

  processRegisteredServers(msg: MsgPayLoad): void {
    this.preReqRegisteredServerTask = JSON.parse(msg.body.msg);

    if (this.preReqRegisteredServerTask.completed && !this.preReqRegisteredServerTask.success) {

      if (this.preReqRegisteredServerTask.errorCode == 'ORPHANED_SERVERS_FOUND') {
        this.errorMessage = $localize`Orphaned servers were found`;
      } else if (this.preReqRegisteredServerTask.errorCode == 'ERROR_VALIDATE_REG_SERVERS') {
        this.errorMessage = $localize`Unable to get registered servers. Please check Content Server logs.`;
      } else if (this.preReqRegisteredServerTask.data) {
        this.errorMessage = $localize`Prerequisite checks failed. Please review the error for each instance.`;
        this.preReqRegisteredServerCheck = JSON.parse(this.preReqRegisteredServerTask.data);
      }

      if (this.preReqRegisteredServerCheck.additionalHosts.length > 0 || this.preReqRegisteredServerCheck.missingHosts.length > 0) {
        //flag all instances as error
        this.preFlightInstanceRows.forEach((row: DataTableRow) => {
          row.error = true;
        });
      }
      this.preReqCheckInProcess = false;
      this.isError = true;
    } else if (this.preReqRegisteredServerTask.completed && this.preReqRegisteredServerTask.success) {
      let registeredServers: CSRegisteredServerHost[] = JSON.parse(this.preReqRegisteredServerTask.data);

      //set registered admin servers
      registeredServers.forEach((csServer: CSRegisteredServerHost) => {
        if (this.targetProductInstances[csServer.macAddress]) {
          this.targetProductInstances[csServer.macAddress].forEach((instance: ProductInstance) => {
            let foundInstances: CSRegisteredServerInstance[] = csServer.instances.filter((csInstance: CSRegisteredServerInstance) => {
              return csInstance.installDirectory == instance.installPath;
            });
            if (foundInstances.length > 0 && foundInstances[0].adminServer) {
              instance.isRegisteredAdminServer = true;
            }
          });
        }
      });

      if (this.upgradePromptResponse == undefined) {
        this.upgradePromptResponse = new UpgradePromptResponse();
        this.upgradePromptResponse.prompts = new Map<string, any>();
      }

      this.preFlightRequest = new PreFlightRequest(
        this.capabilities.supportsHotfix, this.selectedVersion.productId,
        this.selectedVersion.plugin, undefined,
        undefined, this.selectedVersion.platform,
        this.validationData.downloaded.update != undefined ? this.validationData.downloaded.update.id : undefined,
        this.validationData.downloaded.update != undefined ? this.validationData.downloaded.update.name : undefined,
        this.patchesToInstall, this.patchesToBeRemoved,
        this.validationData.downloaded.update != undefined && this.validationData.downloaded.update.details &&
          this.validationData.downloaded.update.details.downloadedLangPackList ?
          this.validationData.downloaded.update.details.downloadedLangPackList : undefined,
        this.targetProductInstances,
        this.selectedVersion.productGroupId,
        this.selectedVersion.patchLevel,
        this.capabilities,
        this.upgradePromptResponse.prompts,
        this.isAcknowledgedSkipMissing ? [] : this.patchesToBeReapplied);
      this.softwareService.sendPreFlight(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
        this.preFlightRequest).pipe(
          tap(resp => {
          }),
          catchError(resp => {
            this.errorMessage = TranslationUtils.getTaskErrorMessage("PRE_FLIGHT", undefined);
            this.isError = true;
            this.preReqCheckInProcess = false;
            return of(resp);
          })).subscribe();
    }
  }

  processPreFlightCheck(msg: MsgPayLoad): void {
    let preFlightTask: PreFlightProgressTask = JSON.parse(msg.body.msg);

    if (preFlightTask.status.completed) {
      this.preReqCheckSuccess = preFlightTask.status.success;
      this.preReqCheckInProcess = false;

      preFlightTask.progress.forEach((progress: PreFlightProgress) => {
        let rows: DataTableRow[] = this.preFlightInstanceRows.filter((row: DataTableRow) => {
          return row.data.system == progress.systemName && row.data.installPath == progress.instance;
        });
        let row = rows[0];

        if (row) {
          if (progress.status.completed) {
            row.data.progress = progress;

            if (progress.status.success) {
              if (progress.status.warning) {
                row.showWarningIcon = true;
              } else {
                row.showSuccessIcon = true;
              }

              let isMaster: boolean = progress.isMasterInstance;
              let checks: PreFlightCheck[] = progress.checks.filter((check: PreFlightCheck) => {
                if (check != undefined && check.type == PreFlightCheckType.SYSTEM_ANALYSIS && isMaster) {
                  this.analysisVersion = check.dataJson.version;
                }
                return check.type == PreFlightCheckType.SYSTEM_ANALYSIS;
              });
              row.data.actions = $localize`View details`;
            } else {
              row.data.actions = $localize`View errors`;
              row.error = true;
              row.errorText = $localize`View errors`;
            }
            this.removeTransaction(msg.headers.transactionId);
          }
        }
      });
    }
  }

  viewPrereqDetails(row: DataTableRow): void {
    this.modal.open(PreReqCsErrorComponent, {
      data: { progress: row.data.progress }
    });
  }

  showPreReqErrorDetails(mode): void {
    Utils.showPreReqErrorDetails(mode, this.preReqRegisteredServerCheck, this.csInfo, this.masterSystem, this.modal, this.dataService);
  }

  setSummary(): void {
    this.applyItemsRows = [];
    if (this.updateToInstall !== undefined) {
      this.applyItemsRows.push(new DataTableRow({
        installType: $localize`Add`,
        name: this.updateToInstall.name,
        description: this.updateToInstall.description,
        type: $localize`Update`,
        size: this.updateToInstall.details.size,
        data: this.updateToInstall
      },
        [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, this.updateToInstall, false, true)], false));
    }

    this.langPacksToInstall.forEach((lp: LanguagePack) => {
      this.applyItemsRows.push(new DataTableRow({
        installType: $localize`Add`,
        name: lp.name,
        description: lp.displayName,
        type: $localize`Language Pack`,
        size: this.fileSize.transform(lp.size),
        data: lp
      }, [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, lp, false, true)], false));
    });

    this.patchesToInstall.forEach((patch: PatchExt) => {
      let tmpPatch: any = patch;
      tmpPatch.type = ObjectDetailsTypes.PATCH;
      this.applyItemsRows.push(new DataTableRow({
        installType: $localize`Add`,
        name: patch.name,
        description: patch.description,
        type: $localize`Patch`,
        size: patch.details.size,
        data: tmpPatch
      }, [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, patch, false, true)], false));
    });

    if (!this.isAcknowledgedSkipMissing) {
      this.patchesToBeReapplied.forEach((patch: PatchExt) => {
        let tmpPatch: any = patch;
        tmpPatch.type = ObjectDetailsTypes.PATCH;
        this.applyItemsRows.push(new DataTableRow({
          installType: $localize`Reapply`,
          name: patch.name,
          description: patch.description,
          type: $localize`Patch`,
          size: patch.details.size,
          data: tmpPatch
        }, [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, patch, false, true)], false));
      });
    }

    this.patchesToBeRemoved.forEach((patch: PatchExt) => {
      let tmpPatch: any = patch;
      tmpPatch.type = ObjectDetailsTypes.PATCH;
      tmpPatch.details = {
        fileName: tmpPatch.fileName,
        publishDate: tmpPatch.publishDate,
        downloadURL: tmpPatch.downloadURL,
        size: tmpPatch.size
      };
      this.applyItemsRows.push(new DataTableRow({
        installType: $localize`Remove`,
        name: tmpPatch.name,
        description: tmpPatch.description,
        type: $localize`Patch`,
        size: tmpPatch.details.size,
        data: tmpPatch
      }, [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, tmpPatch, false, true)], false));
    });

    this.preFlightInstanceRows.forEach(row => {
      row.selected = false;
      row.showSuccessIcon = false;
      row.showWarningIcon = false;
      row.data.actions = '';
    });
    this.isApplyItemsReady = true;
  }

  startSync(): void {
    this.switchSolution(this.targetProduct.environment).then((data) => {
      setTimeout(() => {
        this.sendInstall();
      });
    });
  }

  sendInstall(): void {
    this.isError = false;
    this.errorMessage = "";
    this.isSendingPatchInstall = true;
    this.softwareService.sendPatchInstall(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
      ProductInstallType.SYNC_PATCH_INSTALLATION,
      this.capabilities.supportsHotfix,
      this.targetProduct.productId,
      this.targetProduct.plugin,
      this.csInfo.token,
      this.csInfo.restUrl,
      this.targetProduct.platform,
      this.validationData.downloaded.update != undefined ? this.validationData.downloaded.update.id : undefined,
      this.validationData.downloaded.update != undefined ? this.validationData.downloaded.update.name : undefined,
      this.patchesToInstall,
      this.patchesToBeRemoved,
      this.langPacksToInstall,
      [],
      this.targetProductInstances,
      this.csInfo.info.version,
      this.analysisVersion,
      this.csInfo.isAgent,
      this.targetProduct.productGroupId,
      this.targetProduct.patchLevel,
      this.capabilities,
      this.csInfo.userName,
      this.csInfo.password,
      [],
      false,
      "",
      false,
      [],
      this.patchesToBeReapplied,
      this.isAcknowledgedSkipMissing,
      0).pipe(
        tap(resp => {
          this.isInstalling = true;
          this.isSendingPatchInstall = false;
        }),
        catchError(resp => {
          this.isError = true;
          this.errorMessage = TranslationUtils.getTaskErrorMessage("PATCH_INSTALL", undefined);
          this.isInstalling = false;
          this.isSendingPatchInstall = false;
          return of(resp);
        })).subscribe();

  }

  openOfflineUpload(): void {

    this.modal.open(DragDropComponent, {
      data: new UploadModalData(UploadFileType.MISSING, false,
        this.updateToInstall, this.patchesToInstall, this.langPacksToInstall),
      id: 'otsc-drag-drop-dialog',
      customclass: 'ot-modal-side-panel ot-modal-side-panel-md'
    });
  }

  getValidateHeight(): string {
    let height: string = "calc(100vh - 504px)";
    if (this.preReqRegisteredServerCheck.missingHosts.length > 0) {
      height = "calc(100vh - 566px)";
    }
    return height;
  }

  switchSolution(env: any) {
    const switchPromise = new Promise<void>((resolve) => {
      if (env.id != this.solutionInfo.currentEnvironment.id) {
        this.solutionSrv.switchEnvironment(env.id).pipe(tap(resp => {
          this.currentUserAuthInfo.settings.environmentId = env.id;
          this.currentUserAuthInfo.settings.solutionName = env.solName;
          this.authService.updateLoggedInUser(this.currentUserAuthInfo);
          this.eventService.broadcastLoginStateChange(LoginState.SignedIn);

          let msg = new MsgPayLoad(undefined, {
            message: $localize`Your environment was changed to ${env.solName}:solutionName: : ${env.name}:environmentName:.`
          });
          msg.setEvent(Events.SuccessMsg);

          Utils.addHeaderMessageBanner(this.eventService, msg);
          resolve();
        })).subscribe();
      } else {
        resolve();
      }
    });
    return switchPromise;
  }

  navigateToProducts(): void {
    this.router.navigate(["/products/overview"]);
  }

  getSyncCapabilities(data?: string): void {
    if (this.wsService.isRegistered()) {
      this.syncCapabilityError = false;
      this.softwareService.sendSyncCapabilities(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
        new SyncCapabilitiesRequest(-1, "*", "*", data)).pipe(
          tap(resp => {
          }),
          catchError(resp => {
            this.syncCapabilityError = true;
            return of(resp);
          })).subscribe();
    } else {
      setTimeout(() => {
        this.getSyncCapabilities();
      }, 100);
    }
  }

  isDownloadNextDisabled(): boolean {
    let disabled: boolean = false;
    if (this.isValidatingPackages || this.isError) {
      disabled = true;
    } else if (this.needsDownload) {
      if (this.missingUpdate) {
        disabled = true;
      } else {
        if (this.missingPatchesToInstall > 0) {
          disabled = true;
        } else if (this.missingPatchesToReapply > 0) {
          disabled = this.isAcknowledgedSkipMissing ? false : true;
        }
      }
    }

    return disabled;
  }

  getMissingDownloadMessage(): string {
    let message: string = "";
    let mode: string = this.isOnlineMode ? $localize`downloaded` : $localize`uploaded`;

    if (this.missingPatchesToInstall > 0 && this.missingPatchesToReapply > 0) {
      message = this.missingPatchesToInstall > 1 ? $localize`The following ${this.missingPatchesToInstall} packages to install ` :
        $localize`The following ${this.missingPatchesToInstall} package to install `;
      message += this.missingPatchesToReapply > 1 ? $localize`and ${this.missingPatchesToReapply} packages to reapply must be ${mode} before the system can be synchronized.` :
        $localize`and ${this.missingPatchesToReapply} package to reapply must be ${mode} before the system can be synchronized.`;
    } else if (this.missingPatchesToInstall > 0) {
      message = this.missingPatchesToInstall > 1 ? $localize`The following ${this.missingPatchesToInstall} packages to install must be ${mode} before the system can be synchronized.` :
        $localize`The following ${this.missingPatchesToInstall} package to install must be ${mode} before the system can be synchronized.`;
    } else if (this.missingPatchesToReapply > 0) {
      message = this.missingPatchesToReapply > 1 ? $localize`The following ${this.missingPatchesToReapply} packages to reapply must be ${mode} before the system can be synchronized.` :
        $localize`The following ${this.missingPatchesToReapply} package to reapply must be ${mode} before the system can be synchronized.`;
    } else {
      message = "The missing update below must be downloaded before the system can be synchronized";
    }

    return message;
  }

  showSkipWarningDialog(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          type: ModalMessageType.Warning,
          title: $localize`Warning`,
          message: $localize`You have chosen to skip reapplying patches. This is not the recommended action. Click Yes to proceed.`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px'
        },
        id: 'otsc-sync-skip-reapply-msg'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.goToValidate();
        }
        sub.unsubscribe();
      });
  }
}
